import { Component, computed, effect, inject, input, OnInit, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DesignSystemModule } from '@compeople-shared/design-system-angular';
import { provideTranslocoScope, TranslocoPipe } from '@jsverse/transloco';
import {
  FrameDTO,
  FrameService,
  InventoryDTO,
  LocationDTO,
  LocationTypeDTO,
  ManufacturerDTO,
  ManufacturerService,
  ProductDTO,
  ProductVersionDTO,
  ProductVersionLocationCountDTO,
  ProductVersionOrderDTO,
  ProductVersionService,
  RolesDTO,
} from '@api';
import { ModalService } from '@services/modal.service';
import { noWhitespaceValidator } from '@validators/no-whitespace.validator';
import { ArtworkMetaCardComponent } from '../artwork-meta-card/artwork-meta-card.component';
import { StockLocationPriceFormComponent } from '../../../components/stock-location-price-form/stock-location-price-form.component';
import { ArtButtonComponent } from '../../../components/art-button/art-button.component';
import { ProductStore } from '@stores/product.store';
import { LocationsStore } from '@stores/locations.store';
import { ImageUploadResult, ModalDataInventory } from '@utils/interfaces';
import { UIStore } from '@stores/ui.store';
import { lastValueFrom } from 'rxjs';
import { FrameOption, ModalMode, RefreshChannel } from '@utils/enums';
import { ImageSwitchAreaComponent } from '../../../components/image-switch-area/image-switch-area.component';
import { BorderedImageComponent } from '../../../components/bordered-image/bordered-image.component';
import { ModalModePipe } from '@pipes/modal-mode.pipe';
import { HasRolePipe } from '@pipes/has-role.pipe';
import { RefreshStore } from '@stores/refresh.store';

@Component({
  selector: 'art-artwork-detail-modal',
  imports: [
    DesignSystemModule,
    ReactiveFormsModule,
    TranslocoPipe,
    ArtworkMetaCardComponent,
    StockLocationPriceFormComponent,
    ArtButtonComponent,
    ImageSwitchAreaComponent,
    BorderedImageComponent,
    ModalModePipe,
    HasRolePipe,
  ],
  templateUrl: './artwork-detail-modal.component.html',
  styleUrls: ['./artwork-detail-modal.component.scss'],
  providers: [provideTranslocoScope('artwork')],
})
export class ArtworkDetailModalComponent implements OnInit {
  protected readonly ModalMode = ModalMode;
  protected readonly RolesDTO = RolesDTO;
  readonly modalService = inject(ModalService);
  readonly productStore = inject(ProductStore);
  readonly productVersionService = inject(ProductVersionService);
  readonly refreshStore = inject(RefreshStore);
  readonly manufacturersService = inject(ManufacturerService);
  readonly locationStore = inject(LocationsStore);
  readonly uiStore = inject(UIStore);
  readonly frameService = inject(FrameService);

  data = input<ModalDataInventory | undefined>();
  mode = signal<ModalMode | undefined>(undefined);
  showDragAndDrop = signal(false);

  imageUrl = signal<string | null>(null);

  manufacturerFullName = computed(() => {
    const manufacturer = this.data()?.payload;
    return manufacturer ? `${manufacturer.manufacturerFirstName} ${manufacturer.manufacturerLastName}` : '';
  });

  passepartout: FrameDTO | null = null;
  frame: FrameDTO | null = null;

  imageForm = new FormGroup({
    imageId: new FormControl<string | null>(null),
    imageUrl: new FormControl<string | null>(null),
  });

  originalImageForm: ImageUploadResult | null = null;

  locationsForm = new FormGroup({
    locations: new FormArray<FormGroup>([], Validators.required),
  });

  artworkMetaForm = new FormGroup({
    title: new FormControl(null as null | string, [Validators.required, noWhitespaceValidator()]),
    artist: new FormControl(null as null | ManufacturerDTO, Validators.required),
    year: new FormControl(null as null | number, [Validators.pattern(/^\d{4}$/)]),
    mediumId: new FormControl(null as null | string, Validators.required),
    customsId: new FormControl(null as null | string, Validators.required),
    passepartoutId: new FormControl(null as null | string),
    passepartoutDetails: new FormControl(null as null | string, [noWhitespaceValidator()]),
    frameId: new FormControl(null as null | string),
    frameDetails: new FormControl(null as null | string, [noWhitespaceValidator()]),
    width: new FormControl(null as null | number, Validators.required),
    height: new FormControl(null as null | number, Validators.required),
    depth: new FormControl(null as null | number, Validators.required),
    weight: new FormControl(null as null | number),
    aidaProperty: new FormControl(null as null | boolean),
    closedEdition: new FormControl(null as null | boolean),
  });

  priceForm = new FormGroup({
    customerPrice: new FormControl(null as null | number, Validators.required),
    minimumPrice: new FormControl(null as null | number, Validators.required),
    auctionPrice: new FormControl(null as null | number, Validators.required),
    carriage: new FormControl(null as null | number, Validators.required),
    artistShare: new FormControl(null as null | number, Validators.required),
  });

  showCustomFrame = signal(false);
  showCustomPassepartout = signal(false);
  manufacturer = signal<ManufacturerDTO | null>(null);
  productVersion = signal<ProductVersionDTO | null>(null);
  isLoading = signal(false);
  imageError = signal(false);

  get isFormBlocked() {
    return (
      this.locationsForm.invalid ||
      this.artworkMetaForm.invalid ||
      this.priceForm.invalid ||
      this.imageForm.invalid ||
      this.isLoading()
    );
  }

  constructor() {
    effect(() => {
      const data = this.data();
      if (data) {
        this.mode.set(data.mode);
      }
    });
    effect(() => {
      if (this.showDragAndDrop()) {
        this.imageForm.markAsDirty();
      }
    });
  }

  async ngOnInit() {
    await this.locationStore.fetchLocations();

    const inventory = this.data()?.payload;
    if (!inventory) return;

    await this.loadInventoryDependencies(inventory);

    const product = this.productStore.product();
    const productVersion = this.productVersion();

    await this.loadFrameAndPassepartout(productVersion);

    if (product && productVersion) {
      this.setImageUrl(inventory, productVersion);
      this.fillArtworkMetaForm(product, productVersion);
      this.fillPriceForm(productVersion);
      this.fillLocationsForm();
    }
  }

  async loadInventoryDependencies(inventory: InventoryDTO) {
    const [manufacturer, productVersion] = await Promise.all([
      lastValueFrom(this.manufacturersService.getManufacturersById(inventory.manufacturerId)),
      lastValueFrom(this.productVersionService.getProductVersionById(inventory.productVersionId)),
      this.productStore.fetchProductById(inventory.productId),
      this.uiStore.getArticleCountPerLocationByProduct(inventory.productId),
    ]);
    this.manufacturer.set(manufacturer);
    this.productVersion.set(productVersion);
  }

  async loadFrameAndPassepartout(productVersion: ProductVersionDTO | null) {
    if (!productVersion) return;

    const [frame, passepartout] = await Promise.all([
      productVersion.frameId
        ? lastValueFrom(this.frameService.getFramesById(productVersion.frameId))
        : Promise.resolve(null),
      productVersion.passepartoutId
        ? lastValueFrom(this.frameService.getFramesById(productVersion.passepartoutId))
        : Promise.resolve(null),
    ]);

    this.frame = frame;
    this.passepartout = passepartout;
    this.showCustomFrame.set(this.frame?.isCustom ?? false);
    this.showCustomPassepartout.set(this.passepartout?.isCustom ?? false);
  }

  setImageUrl(inventory: InventoryDTO, productVersion: ProductVersionDTO) {
    if (inventory.productVersionImageURI) {
      const imageUrl = inventory.productVersionImageURI;
      this.imageUrl.set(imageUrl);
      this.imageForm.patchValue({ imageUrl: imageUrl, imageId: productVersion.imageId });
    }
  }

  fillArtworkMetaForm(product: ProductDTO, productVersion: ProductVersionDTO) {
    this.artworkMetaForm.patchValue({
      title: productVersion.title,
      artist: this.manufacturer(),
      year: product.creationYear,
      mediumId: productVersion.mediumId,
      customsId: productVersion.customsId,
      passepartoutId: this.getFrameOrPassepartoutId(
        this.passepartout,
        productVersion.passepartoutId,
        FrameOption.CustomPassepartout,
      ),
      passepartoutDetails: this.passepartout?.description ?? null,
      frameId: this.getFrameOrPassepartoutId(this.frame, productVersion.frameId, FrameOption.CustomFrame),
      frameDetails: this.frame?.description ?? null,
      width: productVersion.width,
      height: productVersion.height,
      depth: productVersion.depth,
      weight: productVersion.weight,
      aidaProperty: product.isAidaProperty,
      closedEdition: !productVersion.isOpenEdition,
    });

    this.disableDetailField('passepartoutDetails');
    this.disableDetailField('frameDetails');
  }

  disableDetailField(detailField: string) {
    const detailControl = this.artworkMetaForm.get(detailField);
    if (detailControl?.value === null) {
      detailControl?.disable();
    }
  }

  getFrameOrPassepartoutId(
    frame: FrameDTO | null | undefined,
    frameId: string | null | undefined,
    customFrameOption: FrameOption,
  ): string | null | undefined {
    if (frame?.isCustom) {
      return customFrameOption;
    }
    return frameId ?? null;
  }

  fillPriceForm(productVersion: ProductVersionDTO) {
    this.priceForm.patchValue({
      customerPrice: productVersion.recommendedPrice,
      minimumPrice: productVersion.minPrice,
      auctionPrice: productVersion.auctionPrice,
      carriage: productVersion.transportCost,
      artistShare: productVersion.manufacturerShare,
    });
  }

  fillLocationsForm() {
    const ffcLocation = this.getFfcLocation();
    const articleCountsForProductVersion = this.getArticleCountsForProductVersion();
    const sortedArticleCountsForProductVersion = this.sortLocationsFfcFirst(articleCountsForProductVersion);

    const stockArray = this.locationsForm.get('locations') as FormArray;
    let hasFfc = false;

    sortedArticleCountsForProductVersion?.forEach((locObj) => {
      stockArray.push(this.createLocationFormGroup(locObj));
      if (!hasFfc) {
        hasFfc = locObj.location.type === LocationTypeDTO.FFC;
      }
    });

    this.insertEmptyFfcIfMissing(stockArray, hasFfc, ffcLocation);
  }

  getFfcLocation() {
    return this.locationStore.paginatedLocations()?.elements.find((loc) => loc.type === LocationTypeDTO.FFC) ?? null;
  }

  getArticleCountsForProductVersion() {
    return this.uiStore
      .articleCountPerLocationByProduct()
      ?.filter((element) => element.productVersionId === this.data()?.payload?.productVersionId);
  }

  sortLocationsFfcFirst(articleCountPerLocationByProductVersion: ProductVersionLocationCountDTO[] | undefined) {
    return articleCountPerLocationByProductVersion?.sort((a, b) =>
      a.location.type === LocationTypeDTO.FFC ? -1 : b.location.type === LocationTypeDTO.FFC ? 1 : 0,
    );
  }

  createLocationFormGroup(locObj: ProductVersionLocationCountDTO): FormGroup {
    return new FormGroup({
      location: new FormControl(locObj.location),
      stock: new FormControl(locObj.count),
    });
  }

  insertEmptyFfcIfMissing(stockArray: FormArray, hasFfc: boolean, ffcLocation: LocationDTO | null) {
    if (!hasFfc && ffcLocation) {
      stockArray.insert(
        0,
        new FormGroup({
          location: new FormControl(ffcLocation),
          stock: new FormControl(0),
        }),
      );
    }
  }

  editArtwork() {
    this.originalImageForm = {
      imageId: this.imageForm.value.imageId ?? null,
      imageUrl: this.imageForm.value.imageUrl ?? null,
    };
    this.mode.set(ModalMode.EDIT);
  }

  viewArtwork() {
    this.imageForm.markAsPristine();
    this.mode.set(ModalMode.VIEW);
  }

  async saveEditedArtwork() {
    if (this.showDragAndDrop()) {
      this.imageForm.patchValue({ imageId: null, imageUrl: null });
      this.imageUrl.set(null);
    }

    const imageId = this.imageForm?.value.imageId;
    if (imageId != null) {
      const oldProductVersion = this.productVersion();
      if (oldProductVersion) {
        const { id, created, lastModified, ...productVersionOrder } = oldProductVersion;
        const newProductVersion = this.buildProductVersion(productVersionOrder);
        if (newProductVersion) {
          this.isLoading.set(true);
          try {
            await lastValueFrom(this.productVersionService.updateProductVersion(id, newProductVersion));
            this.refreshStore.trigger(RefreshChannel.Inventory);
            this.showDragAndDrop.set(false);
            this.viewArtwork();
          } finally {
            this.isLoading.set(false);
          }
        }
      }
    } else {
      this.imageError.set(true);
    }
  }

  cancelEdit() {
    this.imageForm.patchValue({ ...this.originalImageForm });
    this.imageUrl.set(this.imageForm.value.imageUrl ?? null);
    this.imageError.set(false);
    this.viewArtwork();
    this.showDragAndDrop.set(false);
  }

  close() {
    this.modalService.close();
  }

  async uploadImage(event: ImageUploadResult) {
    this.imageError.set(false);
    this.imageForm.patchValue({ imageId: event.imageId, imageUrl: event.imageUrl });
    this.imageForm.markAsDirty();
    this.showDragAndDrop.set(false);
    this.imageUrl.set(event.imageUrl);
    this.imageForm.markAsDirty();
  }

  buildProductVersion(oldProductVersion: ProductVersionOrderDTO) {
    const imageId = this.imageForm?.value.imageId;
    if (imageId != null) {
      return {
        ...oldProductVersion,
        imageId: imageId,
      } as ProductVersionOrderDTO;
    }
    return null;
  }
}
